// SummaryDialogFormazione.jsx - Riepilogo Annuale + Drill-down dettaglio per utente
import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TextField, InputAdornment, IconButton, Tooltip
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleIcon from '@mui/icons-material/People';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { cicassService } from '../../../../_services';
import UserDetailPanel from './UserDetailPanelFormazione';

const SummaryDialogFormazione = ({ open, onClose, year, apiUrl, onToast }) => {
  const [summaryData, setSummaryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');

  // Stato per il drill-down: null = vista riepilogo, oggetto utente = vista dettaglio
  const [selectedUser, setSelectedUser] = useState(null);

  React.useEffect(() => {
    if (open) {
      loadSummaryData();
    } else {
      setSummaryData([]);
      setError(null);
      setSearchText('');
      setSelectedUser(null); // reset drill-down alla chiusura
    }
  }, [open]);

  const loadSummaryData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cicassService.getAll(`${apiUrl}/utente/riepilogo/sede/${year}`);
      const data = Array.isArray(response) ? response : response.data;
      setSummaryData(data);
    } catch (error) {
      console.error('Errore nel caricamento riepilogo:', error);
      setError('Errore nel caricamento del riepilogo');
      onToast('Error', 'Errore nel caricamento del riepilogo');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredData = () => {
    if (!searchText.trim()) return summaryData;
    const searchLower = searchText.toLowerCase().trim();
    return summaryData.filter(item => {
      const name = (item.name || item.nomeUtente || '').toLowerCase();
      return name.includes(searchLower);
    });
  };

  const filteredData = getFilteredData();
  const totaleSummaryOre = filteredData.reduce((sum, item) => sum + (item.oreFatte || 0), 0);

  const handleClose = () => {
    setSummaryData([]);
    setError(null);
    setSearchText('');
    setSelectedUser(null);
    onClose();
  };

  const handleSelectUser = (item) => {
    // Normalizza: serve anaUserId per chiamare l'API di dettaglio
    // Se il tuo riepilogo restituisce già anaUserId usalo direttamente,
    // altrimenti adatta il campo qui sotto.
    setSelectedUser({
      anaUserId: item.anaUserId || item.userId || item.id,
      name: item.name || item.nomeUtente || 'Utente',
    });
  };

  const isDetailView = selectedUser !== null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      {/* ── TITOLO ── */}
      <DialogTitle
        sx={{
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 1.5,
        }}
      >
        <AssessmentIcon sx={{ color: '#2c5282' }} />
        <Typography variant="h6" sx={{ color: '#2c5282', fontWeight: 'bold' }}>
          {isDetailView
            ? `Dettaglio: ${selectedUser.name}`
            : `Riepilogo Formazione ${year}`}
        </Typography>
      </DialogTitle>

      {/* ── CONTENUTO ── */}
      <DialogContent
        sx={{
          p: 0,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {/* VISTA DETTAGLIO UTENTE */}
        {isDetailView ? (
          <UserDetailPanel
            user={selectedUser}
            year={year}
            apiUrl={apiUrl}
            onBack={() => setSelectedUser(null)}
          />
        ) : (
          /* VISTA RIEPILOGO */
          <>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress size={40} />
                <Typography sx={{ ml: 2 }}>Caricamento riepilogo...</Typography>
              </Box>
            )}

            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

            {!loading && !error && summaryData.length === 0 && (
              <Alert severity="info" sx={{ m: 2 }}>
                Nessun dato di formazione trovato per l'anno {year}.
              </Alert>
            )}

            {!loading && !error && summaryData.length > 0 && (
              <>
                {/* Header statistiche aggregate */}
                <Box
                  sx={{
                    bgcolor: '#e3f2fd',
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PeopleIcon sx={{ color: '#1976d2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                      {filteredData.length} volontari formati
                      {searchText && (
                        <Typography component="span" variant="body2" sx={{ ml: 1, color: '#666' }}>
                          (di {summaryData.length} totali)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<AccessTimeIcon />}
                    label={`${totaleSummaryOre} ${totaleSummaryOre === 1 ? 'ora' : 'ore'} totali`}
                    color="primary"
                    sx={{ fontSize: '1rem', fontWeight: 'bold', px: 2, py: 1 }}
                  />
                </Box>

                {/* Barra di ricerca */}
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Cerca volontario per nome..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchText && (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearchText('')} edge="end">
                            <ClearIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  {searchText && filteredData.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                      Nessun volontario trovato con "{searchText}"
                    </Typography>
                  )}
                </Box>

                {/* Tabella riepilogo — riga cliccabile per drill-down */}
                <TableContainer sx={{ overflowY: 'auto' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Volontario</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Ore Totali</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Corsi Completati</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Totale Assenze</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: '#fafafa', width: 48 }}>
                          {/* colonna dettaglio */}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredData
                        .sort((a, b) => (b.oreFatte || 0) - (a.oreFatte || 0))
                        .map((item, index) => (
                          <TableRow
                            key={index}
                            hover
                            onClick={() => handleSelectUser(item)}
                            sx={{
                              cursor: 'pointer',
                              '&:hover': { bgcolor: '#e8f4fd' },
                              '&:nth-of-type(odd)': { bgcolor: 'action.selected' },
                              '&:nth-of-type(odd):hover': { bgcolor: '#e8f4fd' },
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#2c5282' }}>
                                {item.name || item.nomeUtente || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                icon={<AccessTimeIcon />}
                                label={`${item.oreFatte || 0} ${(item.oreFatte || 0) === 1 ? 'ora' : 'ore'}`}
                                color={item.oreFatte > 0 ? 'primary' : 'default'}
                                variant={item.oreFatte > 0 ? 'filled' : 'outlined'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" color="text.secondary">
                                {item.corsiCompletati || item.numeroCorsi || 0}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.totaleAssenze || 0}
                                color={item.totaleAssenze > 0 ? 'error' : 'default'}
                                variant={item.totaleAssenze > 0 ? 'filled' : 'outlined'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ pr: 1 }}>
                              <Tooltip title="Vedi dettaglio corsi">
                                <IconButton
                                  size="small"
                                  sx={{ color: '#2c5282', '&:hover': { bgcolor: '#dbeafe' } }}
                                  onClick={(e) => { e.stopPropagation(); handleSelectUser(item); }}
                                >
                                  <ChevronRightIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </>
        )}
      </DialogContent>

      {/* ── FOOTER ── */}
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        {isDetailView && (
          <Button
            variant="outlined"
            onClick={() => setSelectedUser(null)}
            sx={{ mr: 'auto' }}
          >
            {`← Torna al riepilogo ${year}`}
          </Button>
        )}
        <Button onClick={handleClose} variant="contained">
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SummaryDialogFormazione;